/*
 * This file is part of glowingblue/orell-thalia-common.
 *
 * Copyright (c) Glowing Blue AG.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import Application from 'flarum/common/Application';
import Alert from 'flarum/common/components/Alert';
import LogInModal from 'flarum/forum/components/LogInModal';
import SignUpModal from 'flarum/forum/components/SignUpModal';
import SignUpAlertBody from '../components/SignUpAlertBody';
import { slug } from '../../common';
// `onLogInSuccessCallbacks` / `onSignUpSuccessCallbacks` are installed by
// `loginExtender()`, which declares them on the two modals; it must run first.
import type { AuthSuccessCallbacks } from './loginExtender';

/**
 * Dismisses the sign up alert.
 *
 * @param hide Also persist the dismissal in `localStorage`.
 * @param fromState Skip the `AlertManagerState` call because we are already
 *                  being called from within it.
 */
export type DismissSignUpAlert = (hide?: boolean, fromState?: boolean) => void;

declare module 'flarum/forum/ForumApplication' {
	export default interface ForumApplication {
		/** Set while a sign up alert is on screen, so it can be dismissed from elsewhere. */
		gbDismissSignUpAlert?: DismissSignUpAlert;
	}
}

export default function () {
	// @ts-expect-error -- deliberate patch of the protected `Application.mount`
	extend(Application.prototype, 'mount', () => {
		// Migrate old localStorage that came from `glowingblue/core`
		// TODO : remove this migration code one day
		const oldSlug = 'glowingblue-core';
		if (localStorage.getItem(`${oldSlug}.sign_up_alert.count`)) {
			localStorage.setItem(`${slug}.sign_up_alert.count`, localStorage.getItem(`${oldSlug}.sign_up_alert.count`)!);
			localStorage.removeItem(`${oldSlug}.sign_up_alert.count`);
		}
		if (localStorage.getItem(`${oldSlug}.sign_up_alert.hidden`)) {
			localStorage.setItem(`${slug}.sign_up_alert.hidden`, localStorage.getItem(`${oldSlug}.sign_up_alert.hidden`)!);
			localStorage.removeItem(`${oldSlug}.sign_up_alert.hidden`);
		}

		// If disabled in settings, do nothing
		if (!app.forum.attribute<boolean>(`${slug}.sign_up_alert_enabled`)) {
			return;
		}

		// If a user is logged in, we don't need to show the sign up alert.
		if (app.session.user) {
			return;
		}

		// Verify that the alert has not been dismissed already
		if (localStorage.getItem(`${slug}.sign_up_alert.hidden`)) {
			return;
		}

		// Verify that the user hasn't already seen the alert too many times
		const maxAlerts = app.forum.attribute<number | null>(`${slug}.max_sign_up_alerts`) || 5;
		const alertCount = parseInt(localStorage.getItem(`${slug}.sign_up_alert.count`)!) || 0;
		if (alertCount >= maxAlerts) {
			return;
		}

		// Get the time that we need to wait before showing the alert (in seconds)
		let waitTime = app.forum.attribute<number | null>(`${slug}.sign_up_alert_wait_time`) || 30;
		waitTime *= 1000;

		setTimeout(() => {
			// Is the user logged in in the meantime?
			if (app.session.user) {
				return;
			}

			let alertId: number | undefined;
			const dismiss: DismissSignUpAlert = (hide = false, fromState = false) => {
				if (alertId) {
					if (hide) {
						localStorage.setItem(`${slug}.sign_up_alert.hidden`, 'true');
					}
					if (!fromState) {
						app.alerts.dismiss(alertId);
					}

					delete app.gbDismissSignUpAlert;
				}
			};

			alertId = app.alerts.show(Alert, { type: 'singUp' }, <SignUpAlertBody dismiss={dismiss} />);

			// We might need to run the dismiss function from somewhere else, so we
			// need to make it accessible globally.
			app.gbDismissSignUpAlert = dismiss;

			extend(app.alerts, 'dismiss', function (_, key) {
				if (app.gbDismissSignUpAlert && `${key}` === `${alertId}`) {
					app.gbDismissSignUpAlert(true, true);
				}
			});
		}, waitTime);
	});

	// Users that log in (and therefore already have a user account) should not see the alert.
	const onSignInOrUpSuccessCallback = (callbacks: AuthSuccessCallbacks) => {
		callbacks.add('dismiss-sign-up-alert', () => {
			if (app.gbDismissSignUpAlert) {
				app.gbDismissSignUpAlert(true);
			} else {
				localStorage.setItem(`${slug}.sign_up_alert.hidden`, 'true');
			}
		});
	};
	extend(LogInModal.prototype, 'onLogInSuccessCallbacks', onSignInOrUpSuccessCallback);
	extend(SignUpModal.prototype, 'onSignUpSuccessCallbacks', onSignInOrUpSuccessCallback);
}
